import React, { useState } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { FormControl, Grid, NativeSelect, OutlinedInput, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';

import { useAppDispatch } from '../../../app/hooks/hooks';
import { addFnolReporter, Reporter } from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import { getPolicyInsureds } from '../../../app/store/combinedSelectors';
import { cancelBtn, continueBtn } from '../../../Constants/Constants';
import { FIRST_NAME_REGEX, LAST_NAME_REGEX } from '../../../utils/Regex-Util';
import CallToAction from '../../shared/CallToAction/CallToAction';
import EmailInput from '../../shared/EmailInput/EmailInput';
import PhoneInput from '../../shared/PhoneInput/PhoneInput';
import { RegexTextField } from '../../shared/RegexTextField/RegexTextField';
import { relationshipsToInsured } from './RelationshipToInsured';

export default function ReporterInfo() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [phone, setPhone] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const insureds = useSelector(getPolicyInsureds);
  const [insured, setInsured] = useState('');
  const [contactReferenceKey, setContactReferenceKey] = useState('-1');
  const [insuredIndex, setInsuredIndex] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [relationshipToInsured, setRelationshipToInsured] = useState('');
  const [relationshipToInsuredIndex, setRelationshipToInsuredIndex] = useState('');

  function handleNext() {
    const reporterInfo: Reporter = {
      contactReferenceKey: contactReferenceKey,
      firstName: firstName,
      lastName: lastName,
      emailAddress: email,
      phoneNumber: phone,
      relationshipToInsured: relationshipToInsured,
    };

    dispatch(
      addFnolReporter({
        reporterInfo: reporterInfo,
      })
    );

    navigate('/risk-selection');
  }

  async function handleCancel() {
    navigate('/claims-overview');
  }

  function emailChange(event: string) {
    setEmail(event);
  }

  function emailValid(event: boolean) {
    setIsEmailValid(event);
  }

  function phoneChange(event: string) {
    setPhone(event);
  }

  function phoneValid(event: boolean) {
    setIsPhoneValid(event);
  }

  function handleInsuredChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const indexStr = _get(event, 'target.value');
    const index = Number(indexStr);
    setFirstName(insureds[index].firstName);
    setLastName(insureds[index].lastName);
    setInsured(insureds[index]);
    setInsuredIndex(indexStr);
    setContactReferenceKey(insureds[index].contactReferenceKey);
  }

  function handleFirstNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newFirstName = _get(event, 'target.value');
    setFirstName(newFirstName);
  }

  function handleLastNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newLastName = _get(event, 'target.value');
    setLastName(newLastName);
  }

  function handleRelationshipChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const indexStr = _get(event, 'target.value');
    const index = Number(indexStr);
    setRelationshipToInsured(relationshipsToInsured[index].id);
    setRelationshipToInsuredIndex(indexStr);
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
        maxWidth: '1280px',
        minWidth: '310px',
        margin: '0 auto',
        marginTop: '1em',
      }}
    >
      <Grid item xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType4,
          }}
        >
          Reported by
        </Typography>
      </Grid>

      <Grid sx={{ marginTop: '1em', marginBottom: '1em' }} item xs={12}>
        <Grid container>
          <Grid item xs={12}>
            <Typography sx={theme.custom.styles.formLabelStyle}>Insured</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <NativeSelect
                defaultValue={''}
                input={<OutlinedInput size="small" />}
                variant="outlined"
                value={insuredIndex}
                onChange={handleInsuredChange}
                inputProps={{
                  name: 'Insured',
                  id: 'uncontrolled-native',
                }}
              >
                <option value=""></option>

                {insureds &&
                  insureds.map(
                    (
                      insured: {
                        id: string;
                        firstName: string;
                        lastName: string;
                      },
                      index: number
                    ) => (
                      <option value={index}>
                        <span data-dd-privacy="mask" data-dd-action-name="Insured Name">
                          {insured.firstName + ' ' + insured.lastName}
                        </span>
                      </option>
                    )
                  )}
              </NativeSelect>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      <Grid sx={{ marginTop: '1em', marginBottom: '1em', paddingRight: '1%' }} item xs={6}>
        <Grid container>
          <Grid item xs={12}>
            <Typography sx={theme.custom.styles.formLabelStyle}>First Name</Typography>
          </Grid>
          <Grid item xs={12}>
            <RegexTextField
              id="firstName"
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              value={firstName}
              regex={FIRST_NAME_REGEX}
              changeHandler={handleFirstNameChange}
              inputProps={{
                step: 300, // 5 min
              }}
              sx={{ width: '100%' }}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid sx={{ marginTop: '1em', marginBottom: '1em', paddingLeft: '1%' }} item xs={6}>
        <Grid container>
          <Grid item xs={12}>
            <Typography sx={theme.custom.styles.formLabelStyle}>Last Name</Typography>
          </Grid>
          <Grid item xs={12}>
            <RegexTextField
              id="lastName"
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              value={lastName}
              regex={LAST_NAME_REGEX}
              changeHandler={handleLastNameChange}
              inputProps={{
                step: 300, // 5 min
              }}
              sx={{ width: '100%' }}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid sx={{ marginTop: '1em', marginBottom: '1em' }} item xs={12}>
        <Grid container>
          <Grid item xs={12}>
            <Typography sx={theme.custom.styles.formLabelStyle}>Relationship to Insured</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <NativeSelect
                defaultValue={''}
                input={<OutlinedInput size="small" />}
                variant="outlined"
                value={relationshipToInsuredIndex}
                onChange={handleRelationshipChange}
                inputProps={{
                  name: 'Relationship to Insured',
                  id: 'uncontrolled-native',
                }}
              >
                <option value=""></option>

                {relationshipsToInsured &&
                  relationshipsToInsured.map((insured: { id: string; text: string }, index: number) => (
                    <option value={index}>{insured.text}</option>
                  ))}
              </NativeSelect>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={6} sx={{ width: '99%', paddingRight: '1%' }}>
        <EmailInput
          width={'100%'}
          margin={'0%'}
          isRequired={false}
          hideSubCase={false}
          emailValid={emailValid}
          defaultValue={''}
          emailValueUpdate={emailChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} sx={{ width: '99%', paddingLeft: '1%' }}>
        <PhoneInput
          isRequired={false}
          width={'100%'}
          margin={'0%'}
          hideSubCase={false}
          phoneValid={phoneValid}
          defaultValue={''}
          phoneValueUpdate={phoneChange}
        />
      </Grid>

      <Grid item xs={12}>
        <CallToAction
          nextButton={true}
          nextButtonDisabled={!firstName || !lastName || !relationshipToInsured || !isEmailValid || !isPhoneValid}
          nextButtonContent={continueBtn}
          handleNext={handleNext}
          backButton={false}
          skipButton={false}
          cancelButton={true}
          cancelButtonContent={cancelBtn}
          handleCancel={handleCancel}
        ></CallToAction>
      </Grid>
    </Grid>
  );
}
